import { useState, useEffect } from "react";
import { BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format, addDays } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";

interface InquiryFormProps {
  trigger?: React.ReactNode;
  defaultOpen?: boolean;
}

const circuits = [
  "Self Discovery Circuit",
  "The Devotion Circuit",
  "The Healing Circuit",
  "The Reflection Circuit",
  "The Silence Circuit",
  "The Legacy Circuit",
  "Other",
];

const countries = [
  "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria", "Bangladesh",
  "Belgium", "Brazil", "Canada", "Chile", "China", "Colombia", "Czech Republic", "Denmark",
  "Egypt", "Finland", "France", "Germany", "Greece", "Hong Kong", "Hungary", "India",
  "Indonesia", "Ireland", "Israel", "Italy", "Japan", "Kenya", "Malaysia", "Mexico",
  "Morocco", "Nepal", "Netherlands", "New Zealand", "Nigeria", "Norway", "Pakistan",
  "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Saudi Arabia",
  "Singapore", "South Africa", "South Korea", "Spain", "Sri Lanka", "Sweden", "Switzerland",
  "Taiwan", "Thailand", "Turkey", "UAE", "Ukraine", "United Kingdom", "United States",
  "Vietnam", "Other"
];

export const InquiryForm = ({ trigger, defaultOpen = false }: InquiryFormProps) => {
  const [open, setOpen] = useState(defaultOpen);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();
  
  const [date, setDate] = useState<DateRange | undefined>({
    from: new Date(),
    to: addDays(new Date(), 7),
  });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    circuit: "",
    travelDates: "",
    groupSize: "",
    message: "",
  });

  useEffect(() => {
    if (date?.from) {
      const formattedDate = date.to 
        ? `${format(date.from, "LLL dd, y")} - ${format(date.to, "LLL dd, y")}`
        : format(date.from, "LLL dd, y");
      
      setFormData(prev => ({ ...prev, travelDates: formattedDate }));
    } else {
      setFormData(prev => ({ ...prev, travelDates: "" }));
    }
  }, [date]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = `${BACKEND_URL}/submit_form.php`; 
      
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.status === "success") {
        toast({
          title: t("inquiry.successTitle"),
          description: t("inquiry.successDesc"),
        });

        setFormData({
          name: "",
          email: "",
          phone: "",
          country: "",
          circuit: "",
          travelDates: "",
          groupSize: "",
          message: "",
        });
        setDate(undefined);
        setOpen(false);
      } else {
        throw new Error(result.message || "Failed to submit inquiry");
      }
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="sacred" size="lg">
            {t("nav.requestTour")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto bg-background border-border">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-foreground">
            {t("inquiry.title")}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {t("inquiry.subtitle")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-foreground">{t("inquiry.fullName")} *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Your name"
                className="bg-background border-border"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground">{t("inquiry.email")} *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="you@email.com"
                className="bg-background border-border"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-foreground">{t("inquiry.phone")}</Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+1 234 567 8900"
                className="bg-background border-border"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country" className="text-foreground">{t("inquiry.country")} *</Label>
              <Select
                value={formData.country}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, country: value }))
                }
              >
                <SelectTrigger className="bg-background border-border">
                  <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {countries.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="circuit" className="text-foreground">{t("inquiry.selectCircuit")}</Label>
            <Select
              value={formData.circuit}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, circuit: value }))
              }
            >
              <SelectTrigger className="bg-background border-border">
                <SelectValue placeholder={t("inquiry.selectCircuitPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {circuits.map((circuit) => (
                  <SelectItem key={circuit} value={circuit}>
                    {circuit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="travelDates" className="text-foreground">{t("inquiry.travelDates")}</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id="date"
                  variant={"outline"}
                  className={cn(
                    "w-full justify-start text-left font-normal border-border bg-background hover:bg-accent px-3",
                    !date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date?.from ? (
                    date.to ? (
                      <>
                        {format(date.from, "LLL dd, y")} -{" "}
                        {format(date.to, "LLL dd, y")}
                      </>
                    ) : (
                      format(date.from, "LLL dd, y")
                    )
                  ) : (
                    <span>{t("inquiry.travelDatesPlaceholder")}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={date?.from}
                  selected={date}
                  onSelect={setDate}
                  numberOfMonths={window.innerWidth < 768 ? 1 : 2}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="groupSize" className="text-foreground">{t("inquiry.groupSize")}</Label>
            <Input
              id="groupSize"
              name="groupSize"
              value={formData.groupSize}
              onChange={handleChange}
              placeholder={t("inquiry.groupSizePlaceholder")}
              className="bg-background border-border"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-foreground">
              {t("inquiry.message")}
            </Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder={t("inquiry.messagePlaceholder")}
              rows={4}
              className="bg-background border-border resize-none"
            />
          </div>

          <Button
            type="submit"
            variant="sacred"
            size="lg"
            className="w-full"
            disabled={isSubmitting}
          >
            {isSubmitting ? t("inquiry.submitting") : t("inquiry.submit")}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            By submitting, you agree to receive communications from Bael Leaves.
            We respect your privacy and never share your information.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};